import React, { useEffect, useMemo, useState } from 'react';
import {
    Modal, Segmented, Typography, Space, Tag, Divider, Empty, message,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import {
    FEATURE_GROUPS, BUNDLE_BY_KEY, effectiveFeatures,
} from './featureAccess';
import useBundleFeatures from './useBundleFeatures';
import FeatureTags from './FeatureTags';

const { Text } = Typography;
const { CheckableTag } = Tag;

// Per-user access editor: bundle assignments (per company license) + allow/deny
// feature overrides on top.
const UserFeatureModal = ({
    open, user, licenses = [], initialAssignments, initialOverrides, userHasCalendar = false, onClose, onSave,
}) => {
    // assignments: { [licenseId]: Set-like array of bundleKeys }
    const [byLicense, setByLicense] = useState({});
    const [allow, setAllow] = useState([]);
    const [deny, setDeny] = useState([]);
    const [saving, setSaving] = useState(false);
    // Live bundle→features map (DB overrides), so what a bundle is shown to
    // grant here matches what the backend will actually resolve.
    const { featuresForBundles } = useBundleFeatures();

    useEffect(() => {
        if (!open) return;
        const map = {};
        (initialAssignments || []).forEach((a) => { map[a.licenseId] = [...a.bundleKeys]; });
        setByLicense(map);
        setAllow(initialOverrides?.allow || []);
        setDeny(initialOverrides?.deny || []);
    }, [open, initialAssignments, initialOverrides]);

    const toggleBundle = (licenseId, bundleKey, checked) => {
        setByLicense((prev) => {
            const cur = prev[licenseId] || [];
            return { ...prev, [licenseId]: checked ? [...new Set([...cur, bundleKey])] : cur.filter((k) => k !== bundleKey) };
        });
    };

    const assignments = useMemo(
        () => Object.entries(byLicense)
            .map(([licenseId, bundleKeys]) => ({ licenseId, bundleKeys }))
            .filter((a) => a.bundleKeys.length),
        [byLicense],
    );

    const assignedBundleKeys = useMemo(() => {
        const s = new Set();
        assignments.forEach((a) => a.bundleKeys.forEach((k) => s.add(k)));
        return [...s];
    }, [assignments]);

    const inheritedBase = useMemo(
        () => new Set(featuresForBundles(assignedBundleKeys)),
        [assignedBundleKeys, featuresForBundles],
    );

    const stateFor = (key) => (deny.includes(key) ? 'deny' : allow.includes(key) ? 'allow' : 'inherit');
    const setStateFor = (key, next) => {
        setAllow((prev) => (next === 'allow' ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
        setDeny((prev) => (next === 'deny' ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
    };

    const effective = effectiveFeatures(
        { assignments, allows: allow, denies: deny },
        featuresForBundles,
    );

    const doSave = async () => {
        setSaving(true);
        try {
            const res = await onSave(user.id, { assignments, allow, deny });
            message.success(`Access updated for ${user.name}`);
            if (res && res.calendarRemoved > 0) {
                message.info(`${user.name}’s connected calendar was disconnected`);
            }
            onClose();
        } catch {
            message.error('Failed to update access');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        // Removing interview-scheduling from a user with a connected calendar
        // disconnects that calendar server-side — warn the admin first.
        const willDisconnect = userHasCalendar && !effective.includes('interview-scheduling');
        if (willDisconnect) {
            const confirmed = await new Promise((resolve) => {
                Modal.confirm({
                    title: 'Remove Interview Scheduling and disconnect calendar?',
                    content: `${user?.name || 'This user'} has a connected calendar. Removing Interview Scheduling will disconnect it — their calendar will no longer be bookable for interviews. This can't be undone from here.`,
                    okText: 'Remove & disconnect',
                    okButtonProps: { danger: true },
                    cancelText: 'Keep access',
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
            // Reject the outer Modal's onOk so it stays open when the admin backs out.
            if (!confirmed) throw new Error('cancelled');
        }
        await doSave();
    };

    return (
        <Modal
            title={user ? `Access — ${user.name}` : 'Access'}
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={saving}
            okText="Save access"
            width={680}
            destroyOnClose
        >
            {/* ── Bundle assignments per license ── */}
            <Text strong style={{ fontSize: 13 }}>Bundle assignments</Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                Assign bundles from the company’s licenses. Selecting a bundle uses one seat of that license.
            </Text>
            {licenses.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="This company has no licenses yet — add one under the Licenses tab" />
            ) : licenses.map((lic) => (
                <div key={lic.id} style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, fontWeight: 600 }}>{lic.name}</Text>
                    <div style={{ marginTop: 6 }}>
                        {lic.bundles.filter((bk) => BUNDLE_BY_KEY[bk]?.scope !== 'company').length === 0
                            ? <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>no per-user bundles offered</Text>
                            : (
                                <Space size={[6, 6]} wrap>
                                    {/* company-scoped bundles (ADR-033) are not seats — hidden here, toggled on the AI card */}
                                    {lic.bundles.filter((bk) => BUNDLE_BY_KEY[bk]?.scope !== 'company').map((bk) => (
                                        <CheckableTag
                                            key={bk}
                                            checked={(byLicense[lic.id] || []).includes(bk)}
                                            onChange={(checked) => toggleBundle(lic.id, bk, checked)}
                                        >
                                            {BUNDLE_BY_KEY[bk]?.label || bk}
                                        </CheckableTag>
                                    ))}
                                </Space>
                            )}
                    </div>
                </div>
            ))}

            <Divider style={{ margin: '12px 0' }} />

            {/* ── Feature overrides ── */}
            <Text strong style={{ fontSize: 13 }}>Feature overrides</Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                <b>Inherit</b> follows the assigned bundles; <b>Grant</b> forces a feature on; <b>Deny</b> forces it off.
            </Text>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {Object.entries(FEATURE_GROUPS)
                    // company-scoped features (ADR-033) have no per-user override — they are company-wide
                    .map(([group, features]) => [group, features.filter((f) => f.scope !== 'company')])
                    .filter(([, features]) => features.length > 0)
                    .map(([group, features]) => (
                    <div key={group} style={{ marginBottom: 10 }}>
                        <Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8c8c8c' }}>{group}</Text>
                        {features.map((f) => {
                            const inherited = inheritedBase.has(f.key);
                            return (
                                <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', gap: 12 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                        <Tag color={f.color} style={{ marginInlineEnd: 0 }}>{f.label}</Tag>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{inherited ? 'from bundle' : 'not in bundles'}</Text>
                                    </span>
                                    <Segmented
                                        size="small"
                                        value={stateFor(f.key)}
                                        onChange={(val) => setStateFor(f.key, val)}
                                        options={[{ label: 'Inherit', value: 'inherit' }, { label: 'Grant', value: 'allow' }, { label: 'Deny', value: 'deny' }]}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <Divider style={{ margin: '8px 0 12px' }} />
            <span>
                <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}><LockOutlined /> Effective access</Text>
                <FeatureTags featureKeys={effective} empty="No access" />
            </span>
        </Modal>
    );
};

export default UserFeatureModal;
